import React, { useCallback, useMemo } from "react"

import { supportsTraceContext } from "@/metrics/runtime"
import { Tooltip } from "@coval/ui"
import { Info } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"
import type { TranscriptFilterConfig } from "@/types/metrics/metadata"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../../styles"
import type { FilterPreset } from "../types"
import { FilterList } from "./FilterList"

interface TranscriptFilterSectionProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

/**
 * Hook to manage transcript filter state
 */
function useTranscriptFilter(
  metricRecordData: MetricRecordData,
  onChange: (metric: MetricRecordData) => void,
) {
  const filterConfig = metricRecordData.metric_metadata?.transcript_filter
  const traceContextEnabled =
    metricRecordData.metric_metadata?.trace_context?.enabled === true
  const showTraceContextToggle = supportsTraceContext(metricRecordData)

  const activePreset: FilterPreset = useMemo(() => {
    // Treat trace_context as part of custom scope so saved metrics never
    // hide the toggle behind a "Full" selection.
    if (filterConfig || traceContextEnabled) {
      return "custom"
    }
    return "full"
  }, [filterConfig, traceContextEnabled])

  const handlePresetChange = useCallback(
    (preset: FilterPreset) => {
      if (preset === "full") {
        const {
          transcript_filter: _transcriptFilter,
          trace_context: _traceContext,
          ...restMetadata
        } = metricRecordData.metric_metadata || {}
        onChange({
          ...metricRecordData,
          metric_metadata: restMetadata,
        })
      } else if (preset === "custom" && activePreset !== "custom") {
        onChange({
          ...metricRecordData,
          metric_metadata: {
            ...metricRecordData.metric_metadata,
            transcript_filter: {
              include: [],
              exclude: [],
            },
          },
        })
      }
    },
    [metricRecordData, onChange, activePreset],
  )

  const handleFilterConfigChange = useCallback(
    (newConfig: TranscriptFilterConfig) => {
      // Always update the config - don't auto-remove when empty
      // User must explicitly click "Full" to remove filters
      onChange({
        ...metricRecordData,
        metric_metadata: {
          ...metricRecordData.metric_metadata,
          transcript_filter: newConfig,
        },
      })
    },
    [metricRecordData, onChange],
  )

  const handleTraceContextChange = useCallback(
    (enabled: boolean) => {
      const { trace_context: _traceContext, ...restMetadata } =
        metricRecordData.metric_metadata || {}

      onChange({
        ...metricRecordData,
        metric_metadata: enabled
          ? {
              ...restMetadata,
              trace_context: { enabled: true },
            }
          : restMetadata,
      })
    },
    [metricRecordData, onChange],
  )

  return {
    filterConfig,
    activePreset,
    traceContextEnabled,
    showTraceContextToggle,
    handlePresetChange,
    handleFilterConfigChange,
    handleTraceContextChange,
  }
}

/**
 * Compact toggle for transcript scope - designed to sit inline with other controls
 */
export const TranscriptFilterToggle: React.FC<TranscriptFilterSectionProps> = ({
  metricRecordData,
  onChange,
}) => {
  const { activePreset, handlePresetChange } = useTranscriptFilter(
    metricRecordData,
    onChange,
  )

  const presets: { key: FilterPreset; label: string }[] = [
    { key: "full", label: "Full" },
    { key: "custom", label: "Custom" },
  ]

  return (
    <div className={`${FIELD_CONTAINER_STYLES} flex-1`}>
      <div className={LABEL_ROW_STYLES}>
        <label className={LABEL_STYLES}>Transcript Scope</label>
        <Tooltip
          content={
            <div className="max-w-xs p-1">
              <p className="text-sm">
                Choose which parts of the conversation to evaluate. Use filters
                to focus on specific messages or speakers.
              </p>
            </div>
          }
          side="top"
        >
          <Info className={INFO_ICON_STYLES} />
        </Tooltip>
      </div>

      <div className="flex h-control rounded-md bg-surface-1 p-1">
        {presets.map((preset) => (
          <button
            key={preset.key}
            type="button"
            aria-label={`Transcript scope ${preset.label}`}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handlePresetChange(preset.key)
            }}
            className={`flex-1 px-3 text-sm font-medium rounded-sm transition-all cursor-pointer ${
              activePreset === preset.key
                ? "bg-surface-default text-coval-text shadow-sm"
                : "text-coval-text-muted hover:text-coval-text"
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * Expanded filter details - shows when custom is selected
 */
export const TranscriptFilterDetails: React.FC<
  TranscriptFilterSectionProps
> = ({ metricRecordData, onChange }) => {
  const {
    filterConfig,
    activePreset,
    handleFilterConfigChange,
    traceContextEnabled,
    showTraceContextToggle,
    handleTraceContextChange,
  } = useTranscriptFilter(metricRecordData, onChange)

  if (activePreset !== "custom") {
    return null
  }

  return (
    <div className="w-full">
      <FilterList
        config={filterConfig || { include: [], exclude: [] }}
        onChange={handleFilterConfigChange}
        showTraceContextToggle={showTraceContextToggle}
        traceContextEnabled={traceContextEnabled}
        onTraceContextChange={handleTraceContextChange}
      />
    </div>
  )
}
